import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/Button/Button';
import { cn } from '@/utils/index';
import { useTranslation } from 'react-i18next';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({ className, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    className={cn(
      'bg-fill-background-scrim data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50',
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({ className, children, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Content>) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'dialog',
  });

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'bg-fill-background-neutral data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 tablet:rounded-surface tablet:max-h-[744px] tablet:max-w-[460px] max-tablet:min-w-screen max-tablet:h-dvh tablet:p-0 fixed top-[50%] left-[50%] z-50 flex h-full w-full max-w-full translate-x-[-50%] translate-y-[-50%] flex-col gap-0 overflow-hidden p-0 shadow-lg duration-200',
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className='rounded-button absolute top-6 right-6 disabled:pointer-events-none' asChild>
          <Button variant='icon' size='icon-l' aria-label={t('closeButton.ariaLabel')}>
            <X />
          </Button>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('tablet:text-left flex flex-shrink-0 flex-col gap-2 p-6 pb-0 text-center', className)} {...props} />
);

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-1 flex-col space-y-4 overflow-y-auto p-6', className)} {...props} />
);

interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  withBorder?: boolean;
}

const DialogFooter = ({ className, withBorder = true, ...props }: DialogFooterProps) => (
  <div
    className={cn(
      'tablet:justify-end tablet:space-x-2 desktop:border-none max-tablet:pb-[max(1.5rem,calc(1rem+env(safe-area-inset-bottom)))] flex shrink-0 flex-row gap-3 p-6 pt-4',
      {
        'border-t': withBorder,
      },
      className,
    )}
    {...props}
  />
);

const DialogTitle = ({ className, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title className={cn('text-heading-s-medium flex h-11 items-center', className)} {...props} />
);

const DialogDescription = ({ className, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description className={cn('text-body-m-regular text-text-default-secondary text-left', className)} {...props} />
);

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
